/**
 * The Originals' in-game art: separate sprite pieces drawn in each game's
 * lobby-card style by scripts/art/originals-assets.ts and shipped with the
 * player under public/originals/<game>/ (<piece>@1x.webp, <piece>@2x.webp).
 *
 * A scene asks for the pieces it draws and gets either every one of them
 * decoded, or null — never half a set — so it can keep its vector drawing
 * whenever a file is missing (a deploy without the art, an SPA fallback page
 * answering the image URL, a blocked request).
 */

import PIECE_VERSIONS from './piece-versions.json';

export type Density = 1 | 2;

const versions: Record<string, string> = PIECE_VERSIONS;

/**
 * The piece's URL, carrying its content hash (scripts/art/originals-asset-versions.ts):
 * Pages lets a browser keep these files for four hours, so a redrawn piece must
 * arrive under a new URL or players keep seeing the old one.
 */
export function pieceUrl(game: string, piece: string, density: Density): string {
  const file = `${piece}@${density}x.webp`;
  const version = versions[`${game}/${file}`];
  return `/originals/${game}/${file}${version ? `?v=${version}` : ''}`;
}

/** 2x art for dense screens, where 1x would visibly soften; 1x elsewhere saves the bytes. */
export function preferredDensity(devicePixelRatio: number | undefined): Density {
  return (devicePixelRatio ?? 1) >= 1.5 ? 2 : 1;
}

export type ImageLoader = (url: string) => Promise<HTMLImageElement>;

export const loadImage: ImageLoader = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      // decode() so the first frame that draws it does not stall on decoding.
      img.decode().then(
        () => resolve(img),
        () => resolve(img),
      );
    };
    img.onerror = () => reject(new Error(`image failed: ${url}`));
    img.src = url;
  });

export type PieceImages<K extends string> = Record<K, HTMLImageElement>;

/** Every piece decoded, or null as soon as one fails. */
export async function loadPieces<K extends string>(
  game: string,
  pieces: readonly K[],
  density: Density,
  load: ImageLoader = loadImage,
): Promise<PieceImages<K> | null> {
  try {
    const imgs = await Promise.all(pieces.map((p) => load(pieceUrl(game, p, density))));
    const out = {} as PieceImages<K>;
    pieces.forEach((p, i) => {
      out[p] = imgs[i]!;
    });
    return out;
  } catch {
    return null;
  }
}

const cache = new Map<string, Promise<PieceImages<string> | null>>();

/**
 * One load per game and density for the page's lifetime: remounting a scene
 * (a rows or risk change rebuilds it) reuses the decoded images. A failed set
 * is forgotten so the next mount tries again.
 */
export function preloadPieces<K extends string>(
  game: string,
  pieces: readonly K[],
  density: Density,
  load: ImageLoader = loadImage,
): Promise<PieceImages<K> | null> {
  const key = `${game}@${density}:${pieces.join(',')}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = loadPieces(game, pieces, density, load).then((r) => {
      if (!r) cache.delete(key);
      return r;
    });
    cache.set(key, hit);
  }
  return hit as Promise<PieceImages<K> | null>;
}

/** Test seam: forget every cached set. */
export function _resetPieceCache(): void {
  cache.clear();
}
